import { useRef, useCallback } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Debounce so step-advance + mount don't double-fire, and so a burst of
// note edits collapses into one assessment.
const DEBOUNCE_MS = 1200;

/**
 * AI fit-matrix assessor. POSTs to /triage/calls/:id/assess-fit, which reads the
 * call-so-far notes server-side and grades each fit signal (yes/no/unknown).
 *
 * Dispatches SET_FIT_AI_LOADING / SET_FIT_AI / SET_FIT_AI_ERROR. The endpoint is
 * soft: it returns 200 with a fallback assessment AND an `error` field on any
 * backend trouble (transient LLM error / malformed JSON). On error we do NOT
 * overwrite the displayed verdicts — we keep the last good fitAi and just flag
 * the failure so the matrix never silently blanks on a transient blip. Only a
 * clean response (no `error`) replaces the verdicts.
 *
 * assessFit() — debounced (mount / step advance).
 * assessFit({ immediate }) — fire now (manual re-check button).
 *
 * flushSession — the SAME PATCH the autosave uses (from useTriageSession). We
 * await it before /assess-fit so the LLM grades the fit matrix against exactly
 * what's typed on screen. The handler reads session.question_notes from the DB;
 * without this flush the matrix would stay empty during active typing because
 * the DB snapshot lags up to 10s behind.
 */
export function useFitAssess({ callId, flushSession, dispatch }) {
  const timerRef = useRef(null);
  const inFlightRef = useRef(false);
  const flushRef = useRef(flushSession);
  flushRef.current = flushSession;

  const fire = useCallback(async () => {
    if (!callId || inFlightRef.current) return;
    inFlightRef.current = true;
    dispatch({ type: 'SET_FIT_AI_LOADING', loading: true });
    // Persist live notes to the DB before the LLM reads them. Soft-fail.
    try {
      if (flushRef.current) await flushRef.current();
    } catch (e) {
      console.error('Fit pre-assess flush failed:', e);
    }
    try {
      const res = await axios.post(`${API}/triage/calls/${callId}/assess-fit`);
      // The endpoint soft-fails with a 200 + `error` field. On error, keep the
      // last good verdicts (don't dispatch SET_FIT_AI with the fallback) and flag
      // it instead, so a transient blip never blanks the matrix.
      if (res.data?.error) {
        console.warn('Fit assessment soft-failed:', res.data.error);
        dispatch({ type: 'SET_FIT_AI_ERROR' });
      } else {
        dispatch({ type: 'SET_FIT_AI', assessment: res.data?.assessment || [] });
      }
    } catch (err) {
      // Hard failure (network / non-2xx). Same policy: preserve verdicts, flag it.
      console.error('Fit assessment failed:', err);
      dispatch({ type: 'SET_FIT_AI_ERROR' });
    } finally {
      inFlightRef.current = false;
    }
  }, [callId, dispatch]);

  const assessFit = useCallback((opts = {}) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (opts.immediate) {
      fire();
    } else {
      timerRef.current = setTimeout(() => fire(), DEBOUNCE_MS);
    }
  }, [fire]);

  return { assessFit };
}
